import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createErrorComponent } from "components/Errors/ErrorComponent";
import { WarehouseElementLocation } from "components/WarehouseElements/WarehouseElementsData";
import { TestIds } from "constants/Tests/TestIds";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import { useElementDetails } from "hooks/useElementDetails";
import {
  ElementDetailsAction,
  ElementDetailsValue,
} from "models/Resources/ElementDetailsResource";
import React, { JSX, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePostActionRequestMutation } from "services/api/warehouseApi";
import { selectStoreNb } from "store/Selectors/locationsSelector";
import { setOpenedDetailId } from "store/Slices/gridEventSlice";
import ClipBoardTexte from 'utils/copyClipboard';
import DetailsAction from "./DetailsAction/DetailsAction";
import "./DetailsWindow.scss";
import DynamicTable from "./DynamicTable";

type Detail = {
  label: JSX.Element;
  value: JSX.Element;
};

type WindowInfo = {
  details: Detail[];
  actions: JSX.Element[];
};

type Props = {
  id: string;
  useStoreNb?: number;
  location?: WarehouseElementLocation;
  posTop?: number;
  posLeft?: number;
};

export const DETAIL_WINDOW_ID = "detail-window";
const MAX_DETAIL_WINDOW_WIDTH = 550;

export function DetailsWindow({ id, posTop, posLeft, useStoreNb }: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const storeNb = useAppSelector(selectStoreNb);
  const [isWindowLeftOfElement, setWindowLeftOfElement] = useState(false);
  const [windowInfos, setWindowInfos] = useState<WindowInfo>();
  const [detailPosition, setDetailPosition] = useState({
    top: posTop ?? 0,
    left: posLeft ?? 0,
    bottom: 0,
    position: "right",
  });

  const [
    sendActionRequest,
    {
      isSuccess: isPostSuccess,
      data: postResponse,
      isLoading: isPostLoading,
      error: postError,
    },
  ] = usePostActionRequestMutation();
  let isLastPostResponseObsolete = false;
  function getOffsetInsideParent(
    parentDiv: HTMLElement,
    childDiv: HTMLElement,
  ) {
    // Get the position of the child element relative to the viewport
    const childRect = childDiv.getBoundingClientRect();

    // Get the position of the parent element relative to the viewport
    const parentRect = parentDiv.getBoundingClientRect();

    // The offset is the difference between their viewport positions
    // plus the parent's current scroll position (scrollTop) to account
    // for content that has already scrolled past the top of the parent.
    const offsetTop = childRect.top - parentRect.top + parentDiv.scrollTop;
    const offsetLeft = childRect.left - parentRect.left + parentDiv.scrollLeft;

    return {
      top: offsetTop,
      left: offsetLeft,
    };
  }

  useEffect(() => {
    const warehouseGridSize = document.getElementsByClassName(
      "indiana-scroll-container",
    )[0];
    const refIdLocation = document.getElementById("etiquettesection" + id);
    if (warehouseGridSize && refIdLocation) {
      const offset = getOffsetInsideParent(
        warehouseGridSize as HTMLElement,
        refIdLocation,
      );
      // le 10 représente le margin
      const pos = {
        top: offset.top - 10,
        left: offset.left - 10 + refIdLocation.getBoundingClientRect().width,
        bottom: 0,
        position: "left",
      };
      if (
        pos.left + MAX_DETAIL_WINDOW_WIDTH >
        warehouseGridSize.scrollLeft + warehouseGridSize.clientWidth
      ) {
        pos.left = offset.left - 20 - MAX_DETAIL_WINDOW_WIDTH;
      }
      setDetailPosition(pos);
    } else {
      const pos = {
            top: detailPosition.top,
            left: detailPosition.left,
            bottom: 0,
            position: "left",
          };        
      if (window.innerWidth < detailPosition.left + MAX_DETAIL_WINDOW_WIDTH) {
        pos.left = pos.left - MAX_DETAIL_WINDOW_WIDTH;
      }
      if (window.innerHeight < detailPosition.top + 500) {
        pos.top = 0;
        pos.bottom = window.innerHeight - detailPosition.top;
      }
      setDetailPosition(pos);
    }
  }, [id]);

  useEffect(() => {
    if (isPostLoading && isLastPostResponseObsolete) {
      isLastPostResponseObsolete = false;
    } else if (isPostSuccess && !isLastPostResponseObsolete && postResponse) {
      const responseDetails: Detail[] = [];
      const responseActions: JSX.Element[] = [];
      let idx = 0;
      for (const detail of postResponse.details) {
        idx++;
        responseDetails.push(createDetail(detail, idx));
      }

      if (postResponse.actions) {
        for (const action of postResponse.actions) {
          responseActions.push(createActionButton(action));
        }
      }
      setWindowInfos({ actions: responseActions, details: responseDetails });
    }
  }, [isPostSuccess, isPostLoading]);

  const {
    detailsInfos,
    error: detailError,
    isLoading: isElementDetailsLoading,
  } = useElementDetails(useStoreNb ?? storeNb, id);

  const token = useRef(useAppSelector((state) => state.appState.token));

  if (isElementDetailsLoading) {
    isLastPostResponseObsolete = true;
    return <></>;
  }

  const handleWindowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  //GT: Ajouter onFulfilled dans le then.
  const handleActionClick = async (action: string, params?: any) => {
    try {
      await sendActionRequest({
        storeNb: useStoreNb ?? storeNb,
        elementId: id,
        actionId: action,
        token: token.current,
        parametres: params,
      }).unwrap();
      dispatch(setOpenedDetailId(null));
    } catch (err) {
      console.error("Erreur lors de l'éxecution de l'action", err);
    }
  };

  function createDetail(detail: ElementDetailsValue, idx: number): Detail {

    const vv = detail.valeur.split("^c");
    
    
    const label = (
      <td
        className={`details-labels details-${detail.etiquette}`}
        key={"label-" + detail.etiquette + "-" + detail.valeur}
      >
        {detail.etiquette + ":"}
      </td>
    );

    const value = (
      <td
        id={"details-value-" + idx}
        className={`details-value details-${detail.valeur}`}
        key={"value-" + detail.etiquette + "-" + detail.valeur}
      >
        { detail.valeur && <ClipBoardTexte texte={detail.valeur} ></ClipBoardTexte> }
        { detail.valeurs && ( <DynamicTable data={detail.valeurs} key={"table-" + detail.etiquette + "-" + detail.valeur} /> ) }
      </td>
    );


    return { label, value };
  }

  function getActionButtons() {
    if (windowInfos && !isLastPostResponseObsolete) return windowInfos.actions;
    const detailActions: JSX.Element[] = [];
    detailsInfos?.actions?.map((action) =>
      detailActions.push(createActionButton(action)),
    );
    return detailActions;
  }

  function createActionButton(action: ElementDetailsAction): JSX.Element {
    return (
      <td key={"td" + action.etiquette} className="detail-action-button">
        <DetailsAction
          key={action.etiquette}
          elementAction={action}
          onActionClick={handleActionClick}
        />
      </td>
    );
  }

  function getTableBody() {
    const body = [];
    if (windowInfos && !isLastPostResponseObsolete) {
      for (let i = 0; i < windowInfos.details.length; i++) {
        body.push(
          <tr className="details-body-row" key={i}>
            {windowInfos.details[i].label}
            {windowInfos.details[i].value}
          </tr>,
        );
      }
    } else {
      detailsInfos?.details.map((detail, index) => {
        const detailToRender = createDetail(detail, index);
        body.push(
          <tr
            className="details-body-row"
            key={detail.etiquette + detail.valeur}
          >
            {detailToRender.label}
            {detailToRender.value}
          </tr>,
        );
      });
    }
    return body;
  }
  if (detailError || postError) {
    if (postError && (postError as FetchBaseQueryError).data) {
      return createErrorComponent(postError as FetchBaseQueryError);
    } else {
      return createErrorComponent(detailError as FetchBaseQueryError);
    }
  }

  return createPortal(
    <div
      id={DETAIL_WINDOW_ID}
      data-testid={TestIds.DETAILS_WINDOW}
      className="details-window-wrapper"
      style={{
        ...{ top: detailPosition.top == 0 ? "unset" : detailPosition.top, 
             bottom: detailPosition.bottom == 0 ? "unset" : detailPosition.bottom, 
             left: detailPosition.left },
      }}
    >
      <div className="details-window" tabIndex={-1} onClick={handleWindowClick}>
        <table>
          <tbody className="details-body">{getTableBody()}</tbody>
          <tfoot>
            <tr className="actions-details-buttons">{getActionButtons()}</tr>
          </tfoot>
        </table>
      </div>
    </div>,
    document.getElementById("warehouse-grid")
      ? document.getElementById("warehouse-grid")!
      : document.body,
  );
}
